import React, { useState, useMemo, useRef } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import Hero from "../../components/Hero/Hero";
import PageFooter from "../../components/PageFooter/PageFooter";
import Seo from "../../i18n/Seo";
import useLang from "../../i18n/useLang";
import content from "./content";
import "./structuredData.css";

const DAYS = [
  { key: "monday", en: "Monday" },
  { key: "tuesday", en: "Tuesday" },
  { key: "wednesday", en: "Wednesday" },
  { key: "thursday", en: "Thursday" },
  { key: "friday", en: "Friday" },
  { key: "saturday", en: "Saturday" },
  { key: "sunday", en: "Sunday" },
];

const BUSINESS_TYPES = [
  "LocalBusiness",
  "Restaurant",
  "BarOrPub",
  "CafeOrCoffeeShop",
  "Bakery",
  "FastFoodRestaurant",
  "IceCreamShop",
  "Store",
  "ClothingStore",
  "ElectronicsStore",
  "Florist",
  "FurnitureStore",
  "GroceryStore",
  "HardwareStore",
  "HomeGoodsStore",
  "JewelryStore",
  "LiquorStore",
  "ShoeStore",
  "SportingGoodsStore",
  "AutoRepair",
  "AutoDealer",
  "DaySpa",
  "HairSalon",
  "BeautySalon",
  "NailSalon",
  "TattooParlor",
  "Dentist",
  "MedicalClinic",
  "Pharmacy",
  "Physician",
  "Veterinarian",
  "Optician",
  "AccountingService",
  "Attorney",
  "InsuranceAgency",
  "RealEstateAgent",
  "TravelAgency",
  "Electrician",
  "Plumber",
  "LocksmithService",
  "MovingCompany",
  "HVACBusiness",
  "Hotel",
  "Hostel",
  "Motel",
  "BedAndBreakfast",
  "FitnessCenter",
  "GolfCourse",
  "ChildCare",
  "Library",
  "ShoppingCenter",
];

const REQUIRED_FIELDS = [
  "name",
  "url",
  "telephone",
  "streetAddress",
  "addressLocality",
  "postalCode",
];

const INIT_FORM = {
  name: "",
  url: "",
  images: "",
  telephone: "",
  streetAddress: "",
  addressLocality: "",
  addressRegion: "",
  postalCode: "",
  addressCountry: "",
  facebook: "",
  instagram: "",
  youtube: "",
  x: "",
  tiktok: "",
  id: "",
  priceRange: "",
  latitude: "",
  longitude: "",
  type: "LocalBusiness",
};

const initHours = () =>
  DAYS.reduce(
    (acc, d) => ({
      ...acc,
      [d.key]: { closed: false, allDay: false, slots: [] },
    }),
    {}
  );

const initForm = (t) => ({ ...INIT_FORM, addressCountry: t.defaultCountry });

const exampleHours = ({ closedDays, slots }) =>
  DAYS.reduce((acc, day) => {
    if (closedDays.includes(day.key)) {
      acc[day.key] = { closed: true, allDay: false, slots: [] };
    } else {
      acc[day.key] = {
        closed: false,
        allDay: false,
        slots: slots.map((slot) => ({ ...slot })),
      };
    }
    return acc;
  }, {});

const StructuredData = () => {
  const lang = useLang();
  const t = content[lang];
  const [form, setForm] = useState(() => initForm(t));
  const [hours, setHours] = useState(initHours);
  const [minified, setMinified] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copied, setCopied] = useState(false);
  const formRef = useRef(null);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateDay = (dayKey, updates) => {
    setHours((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], ...updates },
    }));
  };

  const addSlot = (dayKey) => {
    setHours((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: [...prev[dayKey].slots, { opens: "09:00", closes: "18:00" }],
      },
    }));
  };

  const removeSlot = (dayKey, index) => {
    setHours((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: prev[dayKey].slots.filter((_, i) => i !== index),
      },
    }));
  };

  const updateSlot = (dayKey, index, field, value) => {
    setHours((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: prev[dayKey].slots.map((s, i) =>
          i === index ? { ...s, [field]: value } : s
        ),
      },
    }));
  };

  const copyToAll = (dayKey) => {
    const source = hours[dayKey];
    setHours((prev) => {
      const next = { ...prev };
      DAYS.forEach((d) => {
        if (d.key !== dayKey) {
          next[d.key] = {
            closed: source.closed,
            allDay: source.allDay,
            slots: source.slots.map((s) => ({ ...s })),
          };
        }
      });
      return next;
    });
  };

  const resetDay = (dayKey) => {
    updateDay(dayKey, { closed: false, allDay: false, slots: [] });
  };

  const fillExample = () => {
    setForm(t.example.form);
    setHours(exampleHours(t.example));
    setShowAdvanced(true);
  };

  const resetForm = () => {
    setForm(initForm(t));
    setHours(initHours());
    setShowAdvanced(false);
  };

  const jsonLd = useMemo(() => {
    const data = {
      "@context": "https://schema.org",
      "@type": form.type || "LocalBusiness",
    };

    if (form.id) data["@id"] = form.id;
    if (form.name) data.name = form.name;
    if (form.url) data.url = form.url;

    const images = form.images
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (images.length) data.image = images;

    if (form.telephone) data.telephone = form.telephone;
    if (form.priceRange) data.priceRange = form.priceRange;

    const hasAddress =
      form.streetAddress || form.addressLocality || form.postalCode;
    if (hasAddress || form.addressCountry) {
      const address = { "@type": "PostalAddress" };
      if (form.streetAddress) address.streetAddress = form.streetAddress;
      if (form.addressLocality)
        address.addressLocality = form.addressLocality;
      if (form.addressRegion) address.addressRegion = form.addressRegion;
      if (form.postalCode) address.postalCode = form.postalCode;
      if (form.addressCountry) address.addressCountry = form.addressCountry;
      data.address = address;
    }

    if (form.latitude && form.longitude) {
      data.geo = {
        "@type": "GeoCoordinates",
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
      };
    }

    const sameAs = [
      form.facebook,
      form.instagram,
      form.youtube,
      form.x,
      form.tiktok,
    ].filter(Boolean);
    if (sameAs.length) data.sameAs = sameAs;

    const specs = [];
    DAYS.forEach((day) => {
      const h = hours[day.key];
      if (h.closed) return;
      if (h.allDay) {
        specs.push({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: day.en,
          opens: "00:00",
          closes: "23:59",
        });
      } else {
        h.slots.forEach((slot) => {
          if (slot.opens && slot.closes) {
            specs.push({
              "@type": "OpeningHoursSpecification",
              dayOfWeek: day.en,
              opens: slot.opens,
              closes: slot.closes,
            });
          }
        });
      }
    });
    if (specs.length) data.openingHoursSpecification = specs;

    return data;
  }, [form, hours]);

  const jsonLdScript = useMemo(() => {
    const json = minified
      ? JSON.stringify(jsonLd)
      : JSON.stringify(jsonLd, null, 2);
    return `<script type="application/ld+json">\n${json}\n</script>`;
  }, [jsonLd, minified]);

  const onCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(jsonLd, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "structured-data.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const remainingRequired = REQUIRED_FIELDS.filter(
    (f) => !form[f]?.trim()
  ).length;

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="sd-page">
      <Seo
        routeId="schemaGenerator"
        title={t.seo.title}
        description={t.seo.description}
        ogTitle={t.seo.ogTitle}
        ogDescription={t.seo.ogDescription}
      />

      <Hero
        title={t.hero.title}
        subtitle={t.hero.subtitle}
        ctaText={t.hero.cta}
        ctaOnClick={scrollToForm}
      />

      <section className="sd-steps">
        <div className="sd-steps__inner">
          <span className="sd-steps__badge">{t.steps.badge}</span>
          <h2 className="sd-steps__title">{t.steps.title}</h2>
          <div className="sd-steps__grid">
            <div className="sd-steps__card">
              <div className="sd-steps__num">1</div>
              <div className="sd-steps__line" />
              <h3 className="sd-steps__card-title">
                {t.steps.items[0].title}
              </h3>
              <p className="sd-steps__card-desc">
                {t.steps.items[0].desc}
              </p>
            </div>
            <div className="sd-steps__card">
              <div className="sd-steps__num">2</div>
              <div className="sd-steps__line" />
              <h3 className="sd-steps__card-title">
                {t.steps.items[1].title}
              </h3>
              <p className="sd-steps__card-desc">
                {t.steps.items[1].desc}
              </p>
            </div>
            <div className="sd-steps__card">
              <div className="sd-steps__num">3</div>
              <h3 className="sd-steps__card-title">
                {t.steps.items[2].title}
              </h3>
              <p className="sd-steps__card-desc">
                {t.steps.items[2].desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="sd-main" ref={formRef}>
        <div className="sd-main__form">
          {/* Main info */}
          <div className="sd-card">
            <div className="sd-card__header">
              <div className="sd-card__icon">📝</div>
              <h3 className="sd-card__title">{t.form.mainTitle}</h3>
            </div>
            <div className="sd-card__body">
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-name">
                  {t.form.name}
                </label>
                <input
                  className="sd-input"
                  id="sd-name"
                  name="name"
                  placeholder={t.form.namePlaceholder}
                  value={form.name}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-url">
                  {t.form.url}
                </label>
                <input
                  className="sd-input"
                  id="sd-url"
                  name="url"
                  placeholder="https://..."
                  value={form.url}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-images">
                  {t.form.images}
                </label>
                <input
                  className="sd-input"
                  id="sd-images"
                  name="images"
                  placeholder="https://logo.png, https://photo.jpg"
                  value={form.images}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-telephone">
                  {t.form.telephone}
                </label>
                <input
                  className="sd-input"
                  id="sd-telephone"
                  name="telephone"
                  placeholder={t.form.telephonePlaceholder}
                  value={form.telephone}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="sd-card">
            <div className="sd-card__header">
              <div className="sd-card__icon">📍</div>
              <h3 className="sd-card__title">{t.form.addressTitle}</h3>
            </div>
            <div className="sd-card__body">
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-street">
                  {t.form.street}
                </label>
                <input
                  className="sd-input"
                  id="sd-street"
                  name="streetAddress"
                  placeholder={t.form.streetPlaceholder}
                  value={form.streetAddress}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field-row">
                <div className="sd-field">
                  <label className="sd-label" htmlFor="sd-city">
                    {t.form.city}
                  </label>
                  <input
                    className="sd-input"
                    id="sd-city"
                    name="addressLocality"
                    placeholder={t.form.cityPlaceholder}
                    value={form.addressLocality}
                    onChange={handleChange}
                  />
                </div>
                <div className="sd-field">
                  <label className="sd-label" htmlFor="sd-region">
                    {t.form.region}
                  </label>
                  <input
                    className="sd-input"
                    id="sd-region"
                    name="addressRegion"
                    placeholder={t.form.regionPlaceholder}
                    value={form.addressRegion}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="sd-field-row">
                <div className="sd-field">
                  <label className="sd-label" htmlFor="sd-postal">
                    {t.form.postal}
                  </label>
                  <input
                    className="sd-input"
                    id="sd-postal"
                    name="postalCode"
                    placeholder={t.form.postalPlaceholder}
                    value={form.postalCode}
                    onChange={handleChange}
                  />
                </div>
                <div className="sd-field">
                  <label className="sd-label" htmlFor="sd-country">
                    {t.form.country}
                  </label>
                  <select
                    className="sd-select"
                    id="sd-country"
                    value={form.addressCountry}
                    onChange={(e) =>
                      handleSelectChange("addressCountry", e.target.value)
                    }
                  >
                    {t.countries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label} ({c.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Social media */}
          <div className="sd-card">
            <div className="sd-card__header">
              <div className="sd-card__icon">📱</div>
              <h3 className="sd-card__title">{t.form.socialTitle}</h3>
            </div>
            <div className="sd-card__body sd-card__body--compact">
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-facebook">
                  Facebook
                </label>
                <input
                  className="sd-input"
                  id="sd-facebook"
                  name="facebook"
                  placeholder="https://facebook.com/..."
                  value={form.facebook}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-instagram">
                  Instagram
                </label>
                <input
                  className="sd-input"
                  id="sd-instagram"
                  name="instagram"
                  placeholder="https://instagram.com/..."
                  value={form.instagram}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-youtube">
                  YouTube
                </label>
                <input
                  className="sd-input"
                  id="sd-youtube"
                  name="youtube"
                  placeholder="https://youtube.com/..."
                  value={form.youtube}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-x">X/Twitter</label>
                <input
                  className="sd-input"
                  id="sd-x"
                  name="x"
                  placeholder="https://twitter.com/..."
                  value={form.x}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-tiktok">
                  TikTok
                </label>
                <input
                  className="sd-input"
                  id="sd-tiktok"
                  name="tiktok"
                  placeholder="https://tiktok.com/@..."
                  value={form.tiktok}
                  onChange={handleChange}
                />
              </div>
              <p className="sd-hint">
                {t.form.socialHint}
              </p>
            </div>
          </div>

          {/* Opening hours */}
          <div className="sd-card">
            <div className="sd-card__header">
              <div className="sd-card__icon">🕐</div>
              <h3 className="sd-card__title">{t.form.hoursTitle}</h3>
            </div>
            <div className="sd-card__body">
              {DAYS.map((day) => {
                const h = hours[day.key];
                return (
                  <div key={day.key} className="sd-day">
                    <div className="sd-day__header">
                      <span className="sd-day__name">{t.days[day.key]}</span>
                      <div className="sd-day__controls">
                        <label className="sd-toggle">
                          <span className="sd-toggle__label">{t.form.closed}</span>
                          <button
                            type="button"
                            className={`sd-switch${h.closed ? " sd-switch--on" : ""}`}
                            onClick={() =>
                              updateDay(day.key, { closed: !h.closed })
                            }
                          >
                            <span className="sd-switch__thumb" />
                          </button>
                        </label>
                        <label className="sd-toggle">
                          <span className="sd-toggle__label">{t.form.allDay}</span>
                          <button
                            type="button"
                            className={`sd-switch${h.allDay ? " sd-switch--on" : ""}`}
                            onClick={() =>
                              updateDay(day.key, { allDay: !h.allDay })
                            }
                          >
                            <span className="sd-switch__thumb" />
                          </button>
                        </label>
                        <button
                          type="button"
                          className="sd-btn sd-btn--sm sd-btn--secondary"
                          onClick={() => copyToAll(day.key)}
                        >
                          {t.form.copyToAll}
                        </button>
                        <button
                          type="button"
                          className="sd-btn sd-btn--sm sd-btn--ghost"
                          onClick={() => resetDay(day.key)}
                        >
                          {t.form.resetDay}
                        </button>
                      </div>
                    </div>
                    {!h.closed && !h.allDay && (
                      <div className="sd-day__slots">
                        {h.slots.map((slot, idx) => (
                          <div key={idx} className="sd-slot">
                            <input
                              type="time"
                              className="sd-input sd-input--time"
                              value={slot.opens}
                              onChange={(e) =>
                                updateSlot(day.key, idx, "opens", e.target.value)
                              }
                            />
                            <span className="sd-slot__sep">—</span>
                            <input
                              type="time"
                              className="sd-input sd-input--time"
                              value={slot.closes}
                              onChange={(e) =>
                                updateSlot(day.key, idx, "closes", e.target.value)
                              }
                            />
                            <button
                              type="button"
                              className="sd-btn sd-btn--sm sd-btn--ghost sd-btn--danger"
                              onClick={() => removeSlot(day.key, idx)}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="sd-btn sd-btn--primary sd-btn--sm"
                          onClick={() => addSlot(day.key)}
                        >
                          {t.form.addSlot}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="sd-hint">
                {t.form.hoursHint}
              </p>
            </div>
          </div>

          {/* Advanced */}
          <div className="sd-card">
            <div className="sd-card__header">
              <div className="sd-card__icon">⚙️</div>
              <h3 className="sd-card__title">{t.form.advancedTitle}</h3>
              <button
                type="button"
                className="sd-btn sd-btn--sm sd-btn--outline"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? t.form.hide : t.form.show}
              </button>
            </div>
            {showAdvanced && (
              <div className="sd-card__body">
                <div className="sd-field-row">
                  <div className="sd-field">
                    <label className="sd-label" htmlFor="sd-id">
                      {t.form.idLabel}
                    </label>
                    <input
                      className="sd-input"
                      id="sd-id"
                      name="id"
                      placeholder={t.form.idPlaceholder}
                      value={form.id}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="sd-field">
                    <label className="sd-label" htmlFor="sd-priceRange">
                      priceRange
                    </label>
                    <input
                      className="sd-input"
                      id="sd-priceRange"
                      name="priceRange"
                      placeholder={t.form.priceRangePlaceholder}
                      value={form.priceRange}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="sd-field-row">
                  <div className="sd-field">
                    <label className="sd-label" htmlFor="sd-lat">
                      Latitude
                    </label>
                    <input
                      className="sd-input"
                      id="sd-lat"
                      name="latitude"
                      placeholder={t.form.latitudePlaceholder}
                      value={form.latitude}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="sd-field">
                    <label className="sd-label" htmlFor="sd-lng">
                      Longitude
                    </label>
                    <input
                      className="sd-input"
                      id="sd-lng"
                      name="longitude"
                      placeholder={t.form.longitudePlaceholder}
                      value={form.longitude}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="sd-field">
                  <label className="sd-label" htmlFor="sd-type">
                    {t.form.typeLabel}
                  </label>
                  <select
                    className="sd-select"
                    id="sd-type"
                    value={form.type}
                    onChange={(e) =>
                      handleSelectChange("type", e.target.value)
                    }
                  >
                    {BUSINESS_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="sd-actions">
            <button
              type="button"
              className="sd-btn sd-btn--secondary"
              onClick={fillExample}
            >
              {t.form.fillExample}
            </button>
            <button
              type="button"
              className="sd-btn sd-btn--ghost"
              onClick={resetForm}
            >
              {t.form.reset}
            </button>
          </div>
        </div>

        {/* JSON-LD Preview */}
        <div className="sd-main__preview">
          <div className="sd-preview">
            <div className="sd-preview__header">
              <div className="sd-preview__title-row">
                <div className="sd-card__icon">💻</div>
                <h3 className="sd-card__title">{t.preview.title}</h3>
              </div>
              <label className="sd-toggle">
                <span className="sd-toggle__label">{t.preview.minify}</span>
                <button
                  type="button"
                  className={`sd-switch${minified ? " sd-switch--on" : ""}`}
                  onClick={() => setMinified(!minified)}
                >
                  <span className="sd-switch__thumb" />
                </button>
              </label>
            </div>
            <pre className="sd-preview__code">{jsonLdScript}</pre>
            <div className="sd-preview__actions">
              <CopyToClipboard text={jsonLdScript} onCopy={onCopy}>
                <button type="button" className="sd-btn sd-btn--accent">
                  {copied ? t.preview.copied : t.preview.copy}
                </button>
              </CopyToClipboard>
              <button
                type="button"
                className="sd-btn sd-btn--outline"
                onClick={downloadJson}
              >
                {t.preview.download}
              </button>
            </div>
            {remainingRequired > 0 && (
              <p className="sd-preview__remaining">
                {t.preview.remaining(remainingRequired)}
              </p>
            )}
          </div>
        </div>
      </div>

      <section className="sd-seo">
        <div className="sd-seo__inner">
          {t.seoSections}
        </div>
      </section>

      <PageFooter />
    </div>
  );
};

export default StructuredData;
